import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ICliente,
  IFilter,
  IGrupo,
  IQueryParam,
  IUbicacionBase,
} from 'modelos/src';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';

export interface ILuminariasFiltros {
  direccion: string;
  identificacion: string;
  dispositivo: string;
  grupos: string[];
  zonas: string[];
  clientes: string[];
}

export interface IFiltrosDialogData {
  filtros: ILuminariasFiltros;
  // Zonas (ubicaciones categoría Luminarias) ya cargadas por el mapa; el modal
  // solo las presenta. Filtrado real (punto en polígono) vive en el flotante.
  zonas?: IUbicacionBase<'Luminarias'>[];
  // Callback de filtrado en vivo: el modal lo invoca en cada cambio (texto con
  // debounce, selects al instante) para que el mapa se actualice sin cerrar el
  // diálogo. Opcional: sin él, el filtro solo aplica al presionar "Aplicar".
  onLiveChange?: (filtros: ILuminariasFiltros) => void;
}

@Component({
  selector: 'app-filtros-luminarias-modal',
  templateUrl: './filtros-luminarias-modal.component.html',
  styleUrl: './filtros-luminarias-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FiltrosLuminariasModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private lista2 = inject(Lista2Service);
  private cdr = inject(ChangeDetectorRef);
  private zone = inject(NgZone);
  helper = inject(HelperService);
  dialogRef =
    inject<MatDialogRef<FiltrosLuminariasModalComponent>>(MatDialogRef);
  data: IFiltrosDialogData = inject(MAT_DIALOG_DATA);

  public direccion = '';
  public identificacion = '';
  public dispositivo = '';
  public grupos: string[] = [];
  public zonas: string[] = [];
  public clientes: string[] = [];

  public gruposDisponibles: IGrupo[] = [];
  public zonasDisponibles: IUbicacionBase<'Luminarias'>[] = [];
  public grupos$?: Subscription;

  // Chips que NO entran visualmente en la fila → se muestran como badge "+N"
  // que abre el popover. Se recalcula midiendo anchos reales (no por cantidad).
  public ocultos: Record<'grupos' | 'zonas' | 'clientes', number> = {
    grupos: 0,
    zonas: 0,
    clientes: 0,
  };

  // Cajas de los 3 selects (para medir el desborde de chips).
  @ViewChildren('boxSelect') private boxes?: QueryList<ElementRef<HTMLElement>>;
  private ro?: ResizeObserver;

  // Objetos de clientes seleccionados (para el popover); los ids no alcanzan
  // para mostrar nombre/color. Se pueblan desde (clientesChange).
  public clientesObjs: ICliente[] = [];

  // Mide cada caja: deja los chips que entran en la fila y oculta el resto
  // (clase .chip-oculto), guardando cuántos quedaron fuera para el badge "+N".
  private medirChips(): void {
    const boxes = this.boxes?.toArray() ?? [];
    for (const box of boxes) {
      const el = box.nativeElement;
      const campo = el.dataset['campo'] as
        | 'grupos'
        | 'zonas'
        | 'clientes'
        | undefined;
      if (!campo) continue;

      const cont = el.querySelector<HTMLElement>('.ng-value-container');
      const chips = Array.from(el.querySelectorAll<HTMLElement>('.ng-value'));
      if (!cont || !chips.length) {
        this.ocultos[campo] = 0;
        continue;
      }

      // Mostrar todos para medir su ancho real.
      chips.forEach((c) => c.classList.remove('chip-oculto'));
      // Medición por ancho (clientWidth + suma), NO por posición: la geometría
      // (getBoundingClientRect) se corría al abrir el select (aparece el input
      // de búsqueda / scroll interno) y daba 0 ocultos hasta el blur. clientWidth
      // y offsetWidth son estables abierto o cerrado.
      // Al abrir se reserva espacio para el input de búsqueda.
      const abierto = !!el.querySelector('.ng-select-focused, .ng-select-opened');
      const disponible = cont.clientWidth - (abierto ? 30 : 0);
      // Se priorizan los ÚLTIMOS seleccionados: se recorre de derecha a izquierda
      // dejando los del final; los primeros que ya no entran se ocultan → "+N".
      let usado = 0;
      let fuera = 0;
      let desbordo = false;
      for (let i = chips.length - 1; i >= 0; i--) {
        const ancho = chips[i].offsetWidth + 4; // + gap aproximado
        if (i === chips.length - 1) {
          usado += ancho; // el último siempre visible
          continue;
        }
        if (desbordo || usado + ancho > disponible) {
          chips[i].classList.add('chip-oculto');
          fuera++;
          desbordo = true;
        } else {
          usado += ancho;
        }
      }
      this.ocultos[campo] = fuera;
    }
    this.cdr.markForCheck();
  }

  // Recalcula tras el próximo render (ng-select pinta los chips async). Se
  // debouncea a un solo rAF cancelable: al agregar un chip disparan a la vez
  // el (change), el ResizeObserver y boxes.changes; sin esto correrían varias
  // mediciones en frames distintos y producían el parpadeo.
  private medirRaf = 0;
  private programarMedicion(): void {
    this.zone.runOutsideAngular(() => {
      if (this.medirRaf) cancelAnimationFrame(this.medirRaf);
      this.medirRaf = requestAnimationFrame(() => {
        this.medirRaf = requestAnimationFrame(() => {
          this.medirRaf = 0;
          this.zone.run(() => this.medirChips());
        });
      });
    });
  }

  public get gruposSel(): IGrupo[] {
    return this.gruposDisponibles.filter(
      (g) => g._id && this.grupos.includes(g._id),
    );
  }

  public get zonasSel(): IUbicacionBase<'Luminarias'>[] {
    return this.zonasDisponibles.filter(
      (z) => z._id && this.zonas.includes(z._id),
    );
  }

  // Quita un chip desde el popover y refiltra en vivo.
  public quitarGrupo(id?: string): void {
    if (!id) return;
    this.grupos = this.grupos.filter((g) => g !== id);
    this.onSelectChange();
  }
  public quitarZona(id?: string): void {
    if (!id) return;
    this.zonas = this.zonas.filter((z) => z !== id);
    this.onSelectChange();
  }
  public quitarCliente(id?: string): void {
    if (!id) return;
    this.clientes = this.clientes.filter((c) => c !== id);
    this.clientesObjs = this.clientesObjs.filter((c) => c._id !== id);
    this.onSelectChange();
  }

  // Filtrado en vivo del texto: se debouncea para no filtrar en cada tecla.
  private textoDebounce$ = new Subject<void>();
  private textoDebounce$$?: Subscription;

  private snapshotFiltros(): ILuminariasFiltros {
    return {
      direccion: this.direccion,
      identificacion: this.identificacion,
      dispositivo: this.dispositivo,
      grupos: [...this.grupos],
      zonas: [...this.zonas],
      clientes: [...this.clientes],
    };
  }

  private emitirEnVivo(): void {
    this.data?.onLiveChange?.(this.snapshotFiltros());
  }

  // Texto: encola el cambio; el debounce dispara el filtrado en vivo.
  public onTextoChange(): void {
    this.textoDebounce$.next();
  }

  // Selects: aplican al instante (como en el filtro de vehículos).
  public onSelectChange(): void {
    this.emitirEnVivo();
    this.programarMedicion();
  }

  // Abrir/cerrar el select cambia el ancho útil (aparece/desaparece el input de
  // búsqueda) → recalcular el "+N" sin re-emitir el filtro.
  public onSelectToggle(): void {
    this.programarMedicion();
  }

  public async listarGrupos(): Promise<void> {
    const filter: IFilter<IGrupo> = { categoria: 'Luminaria' };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: 'nombre',
      select: 'nombre idCliente color',
      limit: 0,
      includeChildren: true,
    };
    this.grupos$?.unsubscribe();
    this.grupos$ = this.lista2.grupos(query, 30).subscribe((d) => {
      this.gruposDisponibles = d.datos;
      // OnPush: la suscripción corre fuera de un evento de la vista, así que
      // sin markForCheck el ng-select se queda con items vacíos y los valores
      // ya seleccionados no se muestran al reabrir el filtro.
      this.cdr.markForCheck();
      // Los chips preseleccionados recién existen cuando llegan los items.
      this.programarMedicion();
    });
  }

  public aplicar(): void {
    this.dialogRef.close(this.snapshotFiltros());
  }

  public limpiar(): void {
    this.direccion = '';
    this.identificacion = '';
    this.dispositivo = '';
    this.grupos = [];
    this.zonas = [];
    this.clientes = [];
    this.clientesObjs = [];
    // Refleja el limpiado en el mapa al instante.
    this.emitirEnVivo();
    this.programarMedicion();
  }

  public cancelar(): void {
    this.dialogRef.close();
  }

  ngOnInit(): void {
    this.zonasDisponibles = this.data?.zonas ?? [];

    const f = this.data?.filtros;
    if (f) {
      this.direccion = f.direccion ?? '';
      this.identificacion = f.identificacion ?? '';
      this.dispositivo = f.dispositivo ?? '';
      this.grupos = [...(f.grupos ?? [])];
      this.zonas = [...(f.zonas ?? [])];
      this.clientes = [...(f.clientes ?? [])];
    }

    this.textoDebounce$$ = this.textoDebounce$
      .pipe(debounceTime(200))
      .subscribe(() => this.emitirEnVivo());

    this.listarGrupos();
  }

  ngAfterViewInit(): void {
    // Recalcula el desborde al cambiar el ancho del modal (drag/resize) o los
    // chips renderizados.
    this.zone.runOutsideAngular(() => {
      this.ro = new ResizeObserver(() => this.programarMedicion());
      this.boxes?.forEach((b) => this.ro?.observe(b.nativeElement));
    });
    this.boxes?.changes.subscribe(() => {
      this.boxes?.forEach((b) => this.ro?.observe(b.nativeElement));
      this.programarMedicion();
    });
    this.programarMedicion();
  }

  ngOnDestroy(): void {
    this.grupos$?.unsubscribe();
    this.textoDebounce$$?.unsubscribe();
    this.ro?.disconnect();
    if (this.medirRaf) cancelAnimationFrame(this.medirRaf);
  }
}
